import { createSelector } from 'reselect';
import {
  ArmyOrganisationGroup,
  ConstrainedItem,
  ConstraintItemType,
  SelectedElement,
  SelectedItemType,
  sortUnitByPosition,
  Unit,
  UserFavoriteUnit,
} from '@Models';
import {
  getArmyList,
  getArmyOrganisation,
  getSelectableUnits,
  getUserFavoriteUnits,
} from '../edit/selector/editSelector';
import {
  AvailableUnitCategoryView,
  AvailableUnitView,
  FavoriteUnitView,
} from './view/AvailableUnitCategoryView';
import { isHiddenItemConstraint } from '@Selectors/builder/myArmyList/common/constraints/is-hidden-item.constraint';
import { allSeletedElement } from '@Selectors/builder/myArmyList/common/constraints/elements/selectable/all-seleted-element';
import { constrainedOjectsPresenter } from '@Selectors/builder/myArmyList/make/validation/constrained-objects.presenter';

export const createSelectableUnitList = (
  armyOrganisationGroups: Array<ArmyOrganisationGroup>,
  units: Unit[],
  favoriteUnits: UserFavoriteUnit[],
): AvailableUnitCategoryView[] => {
  return armyOrganisationGroups
    .map(
      (organisation) =>
        ({
          id: organisation.organisationId,
          label: organisation.name,
          options: units
            .filter(
              (unit) =>
                unit.principal_organisation_id == organisation.organisationId,
            )
            .map(
              (item) =>
                ({
                  id: item.id,
                  name: item.name,
                  points: item.points,
                  favoriteUnits: favoriteUnits
                    .filter((favorite) => favorite.unitId === item.id)
                    .map(
                      (favorite) =>
                        ({
                          id: favorite.id,
                          name: favorite.name,
                          unitId: favorite.unitId,
                          unitOptions: favorite.unitOptions,
                        }) satisfies FavoriteUnitView as FavoriteUnitView,
                    ),
                }) satisfies AvailableUnitView as AvailableUnitView,
            ),
        }) satisfies AvailableUnitCategoryView as AvailableUnitCategoryView,
    )
    .filter((item) => item.options.length > 0);
};

export const availableUnits = createSelector(
  [
    getArmyList,
    getArmyOrganisation,
    getSelectableUnits,
    constrainedOjectsPresenter,
    getUserFavoriteUnits,
  ],
  (
    armyList,
    armyOrganisation,
    units,
    constrainedObjects,
    favoriteUnits,
  ): Array<AvailableUnitCategoryView> => {
    // Il manque des informations pour faire le calcul
    if (!armyOrganisation) return [];

    const availableUnitByMax = units
      .filter((unit) => {
        const selectedElements = allSeletedElement(
          armyList,
          armyOrganisation?.army_organisation_groups ?? [],
          units,
        );

        const selectedElement: SelectedElement = {
          type: SelectedItemType.ArmyListUnit,
          id: '',
          name: '',

          armyListUnitIds: [],

          count: 1,
          quantity: 1,
          points: 0,

          parents: [],

          constrained: {
            type: ConstraintItemType.Unit,
            id: unit.id,
            name: unit.name,
          } satisfies ConstrainedItem as ConstrainedItem,
        } satisfies SelectedElement as SelectedElement;

        const isHidden = isHiddenItemConstraint(
          unit,
          constrainedObjects,
          selectedElements,
          selectedElement,
        );

        return !isHidden;
      })
      .sort(sortUnitByPosition);

    return createSelectableUnitList(
      armyOrganisation.army_organisation_groups,
      availableUnitByMax,
      favoriteUnits,
    );
  },
);
